import { useEffect } from "react"

const LOCK_CLASS = "scroll-locked"

// How many mounted surfaces currently want the lock. Module state, so there is
// exactly one counter — the property that makes ref-counting trustworthy, and
// the reason this isn't Base UI's own `useScrollLock` from @base-ui/utils: that
// counter lives in a transitive package we don't declare, and a second copy in
// node_modules (react bumping its exact pin past ours) would silently give us
// two lockers and bring the bug below straight back.
let lockCount = 0

/**
 * Locks body scroll while `enabled`, releasing when the last caller releases.
 * For full-screen surfaces that own the viewport without going through
 * Sheet/Dialog — today the two voice takeovers.
 *
 * Deliberately a CLASS rather than an inline style, because this page has
 * another scroll lock in it. Base UI's dialog locker (behind every modal
 * Sheet/Dialog) snapshots body's INLINE overflow when it locks and writes that
 * snapshot back when it unlocks. Two owners of one inline property only survive
 * strict LIFO release, and voice breaks LIFO by design: the mini-player's "End
 * voice chat" sits ON TOP of a covering sheet, so the overlay can unmount while
 * that sheet is still open. With both locks inline, voice restored "" on the way
 * out, then the sheet's unlock restored the "hidden" it had snapshotted from
 * voice — with nobody left to release it. The page stayed unscrollable, and the
 * next sheet cycle re-snapshotted the same value, so only a reload cured it.
 *
 * A stylesheet rule can't be captured by that snapshot: Base UI only ever reads
 * and writes inline style. So the two locks stop interacting at all rather than
 * having to agree on ordering — while a sheet is open its inline `hidden`
 * outranks our rule anyway, and when it restores, our rule is still standing if
 * the surface underneath still wants it. See `body.scroll-locked` in globals.css.
 */
export function useBodyScrollLock(enabled = true) {
  useEffect(() => {
    if (!enabled) {
      return
    }

    lockCount += 1
    if (lockCount === 1) {
      document.body.classList.add(LOCK_CLASS)
    }

    return () => {
      lockCount -= 1
      if (lockCount === 0) {
        document.body.classList.remove(LOCK_CLASS)
      }
    }
  }, [enabled])
}
